import {
  Component, Inject, OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, NgFor } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DentalCredentialsService } from '../../../wizard/services/dental-credentials.service';
import { DocumentCategory, DocumentSubCategory, DocumentSubCategoryName } from '../../../../shared/constants/document.constants';

@Component({
  selector: 'app-preview-modal',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    NgFor
  ],
  templateUrl: './document-modal.component.html',
  styleUrls: ['./document-modal.component.scss']
})

export class DocumentPreviewComponent implements OnInit {

  documents: any[] = [];
  isLoading: boolean = false;
  isDownloadingAll: boolean = false;
  downloadAllErrorMessage: string = '';

  constructor(
    private dentalCredentialingService: DentalCredentialsService, private dialogRef: MatDialogRef<DocumentPreviewComponent>, @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    if (this.data?.locationId) {
      this.loadLocationDocumentsFromApi(this.data.locationId);
    } else {
      this.loadDocuments();
    }  }

    loadLocationDocumentsFromApi(locationId: any) {
      this.isLoading = true;
      this.downloadAllErrorMessage = '';
      this.dentalCredentialingService.getLocationDocuments(locationId).subscribe({
          next: (response: any) => {
              const rawDocs = this.normalizeApiResponseToDocuments(response);
              this.documents = rawDocs
                .map((doc: any) => this.normalizeDocumentShape(doc))
                .filter((doc: any) => doc && doc.documentId !== null && !this.isW9Document(doc));
  
              this.isLoading = false;
          },
          error: (err: any) => {
              console.error('Failed to load location documents', err);
              this.isLoading = false;
          }
      });
  }
  private isW9Document(document: any): boolean {
    const subCategoryId = document?.SubCategoryID ?? document?.subCategoryID ?? document?.subCategoryId;
    const subCategoryName = document?.SubCategoryName ?? document?.subCategoryName ?? document?.subcategory;
    return Number(subCategoryId) === DocumentSubCategory.W9Form || subCategoryName === 'W9Form';
  }
  loadDocuments() {
    let storedData = JSON.parse(localStorage.getItem('documents')!);
    if (storedData) {
      this.documents = storedData.map((doc: any) => this.normalizeDocumentShape(doc));
      this.documents = this.filterLatestDocuments(this.documents);
      this.documents = this.documents.sort((a: any, b: any) => Number(b.categoryId) - Number(a.categoryId))
    }
    this.downloadAllErrorMessage = '';
  }

  formatSubCategory(value: string, fileName: any, type: any): string {

    if (value == 'CredentialingApplication') {
      if (type == 'file name') { value = this.formatFileName(fileName) }
      else { value = 'Credentialing Application' }
      return value;
    } else {
      return (value || '')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    }
  }

  onViewDocument(doc: any, fileName: string) {
    const resolvedFileName = fileName || doc?.fileName || doc?.FileName || '';
    const fileKey = this.getFileKey(doc, resolvedFileName);
    if (!fileKey) {
      this.downloadAllErrorMessage = 'Unable to download the selected document. Please try again.';
      return;
    }

    this.downloadAllErrorMessage = '';
    this.dentalCredentialingService.viewAzureDocument(fileKey).subscribe({
        next: (response: any) => {
            const blob = new Blob([response.body], { type: 'application/pdf' });
            this.triggerBlobDownload(blob, this.getDownloadName(resolvedFileName));
        },
        error: (err: any) => {
          console.error('Failed to download document', err);
          this.downloadAllErrorMessage = 'Unable to download the selected document. Please try again.';
        }
    });
}

  get canShowDownloadAll(): boolean {
    return this.documents.length > 0;
  }

  async downloadAllDocuments() {
    if (!this.canShowDownloadAll || this.isDownloadingAll) {
      return;
    }

    this.isDownloadingAll = true;
    this.downloadAllErrorMessage = '';
    const failedDocuments: string[] = [];

    try {
      for (const doc of this.documents) {
        const fileName = doc?.fileName || doc?.FileName || '';
        const fileKey = this.getFileKey(doc, fileName);
        if (!fileKey) {
          failedDocuments.push(fileName || 'Unknown document');
          continue;
        }

        try {
          const response: any = await firstValueFrom(this.dentalCredentialingService.viewAzureDocument(fileKey));
          const blob = new Blob([response.body], { type: 'application/pdf' });
          this.triggerBlobDownload(blob, this.getDownloadName(fileName));
        } catch (err: any) {
          console.error('Failed to download document', err);
          failedDocuments.push(fileName || 'Unknown document');
        }
      }
    } finally {
      this.isDownloadingAll = false;
    }

    if (failedDocuments.length > 0) {
      this.downloadAllErrorMessage = failedDocuments.length === this.documents.length
        ? 'Unable to download documents. Please try again.'
        : `${failedDocuments.length} document(s) could not be downloaded. Please retry them individually.`;
    }
  }

  downloadFile(fileName: string) {
    const url = `/api/document/download/${fileName}`;
    window.open(url, '_blank');
  }

  close() {
    this.dialogRef.close();
  }

  filterLatestDocuments(documents: any[]): any[] {
    const targetCategoryIds = new Set<number>([
      DocumentCategory.DentalCredential,
      DocumentCategory.SignedDocument
    ]);
    const latestMap = new Map<string, any>();
    const others: any[] = [];
   
    for (const doc of documents) {
      const categoryId = Number(doc?.categoryId);
      const subCategoryKey = doc?.subcategory || doc?.subCategoryName || doc?.subCategoryId || '';
      const key = `${categoryId}_${subCategoryKey}`;

      if (targetCategoryIds.has(categoryId)) {
        if (!latestMap.has(key) || latestMap.get(key).documentId < doc.documentId) {
          latestMap.set(key, doc);
        }
      } else {
        others.push(doc);
      }
    }
    return [
      ...Array.from(latestMap.values()),
      ...others
    ];
  }

  formatFileName(fileName: string): string {
    if (!fileName) return 'Credentialing Application';
    const nameWithoutExt = fileName.replace('.pdf', '');
    const parts = nameWithoutExt.split('_');
    const datePart = parts[0];

    if (!datePart || datePart.length !== 8) return 'Credentialing Application';
    const year = datePart.substring(0, 4);
    const month = datePart.substring(4, 6);
    const day = datePart.substring(6, 8);
    return `Credentialing Application_${month}_${day}_${year}`;
  }

  private getFileKey(doc: any, fileName: string): string {
    if (this.isUniformRequirementDocument(doc)) {
      return 'ddmo_uniform_requirements.pdf';
    }

    return fileName ? fileName.split('.')[0] : '';
  }

  private isUniformRequirementDocument(doc: any): boolean {
    const subCategoryId = doc?.subCategoryId ?? doc?.SubCategoryID ?? doc?.subCategoryID;
    return Number(subCategoryId) === DocumentSubCategory.UniformRequirements;
  }

  private normalizeApiResponseToDocuments(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (response) {
      return [response];
    }

    return [];
  }

  private normalizeDocumentShape(doc: any): any {
    const categoryIdRaw = doc?.categoryId ?? doc?.CategoryID ?? doc?.categoryID;
    const categoryId = Number(categoryIdRaw);
    const normalizedCategoryId = Number.isNaN(categoryId) ? null : categoryId;
    const subCategoryIdRaw = doc?.subCategoryId ?? doc?.subCategoryID ?? doc?.SubCategoryID;
    const subCategoryId = Number(subCategoryIdRaw);
    const normalizedSubCategoryId = Number.isNaN(subCategoryId) ? null : subCategoryId;
    const resolvedSubCategoryName = doc?.subcategory
      ?? doc?.subCategoryName
      ?? doc?.SubCategoryName
      ?? (normalizedSubCategoryId != null ? DocumentSubCategoryName[normalizedSubCategoryId] : '');
    const documentIdRaw = doc?.documentId ?? doc?.DocumentID ?? doc?.documentID;
    const documentId = Number(documentIdRaw);
    const normalizedDocumentId = Number.isNaN(documentId) ? null : documentId;

    const resolvedCategoryName = doc?.category
      ?? doc?.categoryName
      ?? doc?.CategoryName
      ?? (normalizedCategoryId != null
        ? (
            normalizedCategoryId === DocumentCategory.DentalCredential ? 'DentalCredential'
            : normalizedCategoryId === DocumentCategory.SignedDocument ? 'SignedDocument'
            : normalizedCategoryId === DocumentCategory.CredentialingApplication ? 'CredentialingApplication'
            : ''
          )
        : '');

    return {
      ...doc,
      subcategory: resolvedSubCategoryName || '',
      subCategoryName: resolvedSubCategoryName || '',
      subCategoryId: normalizedSubCategoryId,
      categoryId: normalizedCategoryId,
      category: resolvedCategoryName || '',
      fileName: doc?.fileName ?? doc?.FileName ?? doc?.originalFileName ?? doc?.OriginalFileName ?? '',
      documentId: normalizedDocumentId
    };
  }

  private getDownloadName(fileName: string): string {
    const sanitizedFileName = fileName?.trim();
    if (!sanitizedFileName) {
      return `document-${Date.now()}.pdf`;
    }

    return sanitizedFileName.endsWith('.pdf') ? sanitizedFileName : `${sanitizedFileName}.pdf`;
  }

  private triggerBlobDownload(blob: Blob, downloadName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

}
